/**
 * Desktop-shell external-open bridge.
 *
 * Inside the Vestra desktop shell (the Tauri WKWebView) a raw
 * `window.open(url, '_blank')` — or a click on `<a target="_blank">` — has nowhere
 * to render: the webview has no tab/popup surface, so the "new tab" opens INVISIBLY.
 * That's why Composio "Connect" OAuth, file downloads, and link-preview "open in new
 * tab" appear to do nothing in the native app.
 *
 * This module routes such external opens to the user's real default browser via the
 * Tauri `opener` plugin. `opener:default` is already granted to the remote origin in
 * the shell's `capabilities/default.json` (it whitelists http/https/mailto/tel at the
 * scheme level), and `withGlobalTauri: true` exposes `window.__TAURI__.core.invoke`
 * to the remote page — so no native change is needed. OAuth providers (Google, etc.)
 * also reject embedded webviews, so the system browser is the correct surface anyway;
 * Composio connection completion flows back via the server `/verify` + Supabase
 * Realtime resume, independent of where the OAuth tab lives.
 *
 * Everything here is inert in a normal browser: `installExternalOpenInterceptor` is
 * only called when `window.__TAURI__` is present (see AppShell), and
 * `openExternalViaShell` no-ops (returns false) when it isn't.
 */

interface TauriCore {
  invoke?: (cmd: string, args?: Record<string, unknown>) => Promise<unknown>;
}
interface TauriGlobal {
  core?: TauriCore;
}

function tauriInvoke(
  win: Window
): ((cmd: string, args?: Record<string, unknown>) => Promise<unknown>) | null {
  const tauri = (win as unknown as { __TAURI__?: TauriGlobal }).__TAURI__;
  return tauri?.core?.invoke ?? null;
}

/** True when running inside the Tauri desktop shell (it injects `window.__TAURI__`). */
export function isDesktopShell(win: Window = window): boolean {
  return (
    typeof (win as unknown as { __TAURI__?: unknown }).__TAURI__ !== "undefined"
  );
}

/**
 * Resolve a requested `_blank`/new-window open to the absolute URL that should be
 * handed to the OS, or `null` if it should stay inside the webview.
 *
 * Routed: `mailto:`/`tel:` (default app) and ANY `http(s)` URL — including
 * same-origin. A `_blank`/`window.open` always asks for a *separate surface*, and
 * the WKWebView has none (no tabs), so the system browser is the only place it can
 * be visible. This covers cross-origin OAuth (Composio) AND same-origin file opens
 * — the file viewer's "open in new tab" hits the public, shareable `/v/{fileId}`
 * proxy, which renders fine in the system browser. (In-app SPA navigation does NOT
 * go through here — it uses the router / plain anchors without `target="_blank"`.)
 * Kept in-webview: non-web schemes (`about:`, `blob:`, `javascript:`, `data:`).
 */
function externalTarget(rawUrl: string, win: Window): string | null {
  const raw = rawUrl.trim();
  if (!raw) return null;
  if (/^(mailto|tel):/i.test(raw)) return raw;
  let url: URL;
  try {
    url = new URL(raw, win.location.href);
  } catch {
    return null;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  return url.href;
}

/**
 * Open an external URL in the OS default application via the Tauri opener plugin.
 * Returns `true` if handed off to the shell, `false` when not in a shell (so callers
 * can fall back to native `window.open`).
 */
export function openExternalViaShell(
  url: string,
  win: Window = window
): boolean {
  const invoke = tauriInvoke(win);
  if (!invoke) return false;
  // opener v2 command + args: `plugin:opener|open_url` { url, with? }.
  void invoke("plugin:opener|open_url", { url }).catch((err: unknown) => {
    // Never throw into the click/handler call site; just surface for diagnostics.
    console.error("[desktop-shell] open_url failed", url, err);
  });
  return true;
}

/**
 * A Window-like stand-in returned from the patched `window.open`. Callers inspect the
 * handle — `if (!popup) …` (treat falsy as "popup blocked"), `popup.closed`,
 * `popup.focus()` — so a real OS browser tab DID open; we just can't hand back its
 * Window object. A truthy stub keeps every caller's success path intact.
 */
function openedStub(): Window {
  return {
    closed: false,
    focus() {},
    blur() {},
    close() {},
    postMessage() {},
  } as unknown as Window;
}

/**
 * Patch `window.open` and intercept `<a target="_blank">` clicks so external opens go
 * to the system browser instead of an invisible webview tab. Returns an uninstall fn.
 *
 * Call ONLY inside the desktop shell (guard on {@link isDesktopShell}); in a normal
 * browser the native behavior must be left untouched.
 */
export function installExternalOpenInterceptor(
  win: Window = window
): () => void {
  // Keep the exact original reference (unbound) so uninstall restores identity; call
  // it through `.call(win, …)` rather than pre-binding a wrapper.
  const originalOpen = win.open;

  const patchedOpen: typeof win.open = (
    url?: string | URL,
    target?: string,
    features?: string
  ) => {
    const href =
      url == null ? "" : typeof url === "string" ? url : url.toString();
    const external = href ? externalTarget(href, win) : null;
    if (external && openExternalViaShell(external, win)) {
      return openedStub();
    }
    return originalOpen.call(
      win,
      url as string,
      target as string,
      features as string
    );
  };
  win.open = patchedOpen;

  const onClick = (event: MouseEvent): void => {
    if (event.defaultPrevented) return;
    const anchor = (event.target as Element | null)?.closest?.("a");
    if (!anchor || anchor.getAttribute("target") !== "_blank") return;
    const external = externalTarget(anchor.getAttribute("href") ?? "", win);
    if (external && openExternalViaShell(external, win)) {
      event.preventDefault();
    }
  };
  // Capture phase so we win before in-app handlers / the browser's default.
  win.document.addEventListener("click", onClick, true);

  return () => {
    win.open = originalOpen;
    win.document.removeEventListener("click", onClick, true);
  };
}
